// Saved views API — thin wrapper over the app_saved_views table (migration
// 095). Direct client CRUD is intentional here: saved views are user
// preferences, not audit-critical entity writes, and RLS scopes every
// operation to public-or-owner SELECT + owner-only INSERT/UPDATE/DELETE.
//
// Ownership is server-trusted: the table's BEFORE INSERT trigger stamps
// owner_profile_id = auth.uid(), so callers MUST NOT send owner_profile_id —
// any client value is overwritten. The caller passes the shared `sb` client;
// no new Supabase client is created here.

import type {SupabaseClient} from './supabaseTypes.js';

const VALID_VISIBILITY = new Set(['private', 'public']);

// view_state is opaque jsonb to the DB. The app stores {filters, sortRules,
// viewMode}. This shape is documented here so callers agree on the contract.
// `filters` stays `unknown`: the surface that wrote it is the only thing that
// knows its keys, this module never looks inside, and the DB treats it as
// opaque — claiming a key set here would be an invention, not a contract.
export type SavedViewState = {
  filters: unknown;
  sortRules: unknown[];
  viewMode: 'flat' | 'grouped';
};

// Every field arrives from caller-held UI state and is validated below, so the
// input side is `unknown` — that is what makes the checks real rather than
// decorative.
export type BuildViewStateInput = {filters?: unknown; sortRules?: unknown; viewMode?: unknown};

export function buildViewState({filters, sortRules, viewMode}: BuildViewStateInput): SavedViewState {
  return {
    filters: filters && typeof filters === 'object' ? filters : {},
    sortRules: Array.isArray(sortRules) ? sortRules : [],
    viewMode: viewMode === 'flat' ? 'flat' : 'grouped',
  };
}

// The five reads/writes below all name the same explicit column list, but this
// module never touches the returned columns, so rows stay `unknown` rather than
// restating that list as a type TypeScript cannot check against the database.

type MaybeAbortable<Builder> = {then: unknown; abortSignal?: (signal: AbortSignal) => Builder};

// Keep plain fakes usable in tests while allowing the shared Query owner to
// cancel an abandoned authenticated picker request.
const withSignal = <Builder extends MaybeAbortable<Builder>>(builder: Builder, signal?: AbortSignal): Builder =>
  signal && typeof builder.abortSignal === 'function' ? builder.abortSignal(signal) : builder;

export async function listSavedViews(sb: SupabaseClient, surfaceKey: string, signal?: AbortSignal): Promise<unknown[]> {
  if (!sb) throw new Error('listSavedViews: sb required');
  if (!surfaceKey) throw new Error('listSavedViews: surfaceKey required');
  // RLS returns only public-or-owner rows. Order public-then-private by name so
  // the picker is stable; ownership/visibility split is done in the UI.
  const {data, error} = await withSignal(
    sb
      .from('app_saved_views')
      .select('id, surface_key, name, visibility, view_state, owner_profile_id, created_at, updated_at')
      .eq('surface_key', surfaceKey)
      .order('name', {ascending: true}),
    signal,
  );
  if (error) throw new Error(`listSavedViews: ${error.message || String(error)}`);
  return data || [];
}

export type CreateSavedViewInput = {
  surfaceKey?: string;
  name?: string | null;
  visibility?: string;
  viewState?: unknown;
};

export async function createSavedView(
  sb: SupabaseClient,
  {surfaceKey, name, visibility, viewState}: CreateSavedViewInput,
): Promise<unknown> {
  if (!sb) throw new Error('createSavedView: sb required');
  if (!surfaceKey) throw new Error('createSavedView: surfaceKey required');
  const trimmed = (name || '').trim();
  if (!trimmed) throw new Error('createSavedView: name required');
  // The added truthiness test is not a new rule: `VALID_VISIBILITY.has()` was
  // already false for undefined and for '', so both still land on 'private'. It
  // is here because `Set<string>.has` cannot be asked about an absent value.
  const vis = visibility && VALID_VISIBILITY.has(visibility) ? visibility : 'private';
  // owner_profile_id intentionally omitted — the DB trigger stamps auth.uid().
  const {data, error} = await sb
    .from('app_saved_views')
    .insert({surface_key: surfaceKey, name: trimmed, visibility: vis, view_state: viewState || {}})
    .select('id, surface_key, name, visibility, view_state, owner_profile_id, created_at, updated_at')
    .single();
  if (error) throw new Error(`createSavedView: ${error.message || String(error)}`);
  return data;
}

export type UpdateSavedViewInput = {
  name?: string | null;
  visibility?: string | null;
  viewState?: unknown;
};

// The patch is assembled key by key, so it is typed by what this function is
// allowed to send — not by the table.
type SavedViewPatch = {name?: string; visibility?: string; view_state?: unknown};

export async function updateSavedView(
  sb: SupabaseClient,
  id: string,
  {name, visibility, viewState}: UpdateSavedViewInput = {},
): Promise<unknown> {
  if (!sb) throw new Error('updateSavedView: sb required');
  if (!id) throw new Error('updateSavedView: id required');
  const patch: SavedViewPatch = {};
  if (name != null) {
    const trimmed = String(name).trim();
    if (!trimmed) throw new Error('updateSavedView: name cannot be blank');
    patch.name = trimmed;
  }
  if (visibility != null) {
    if (!VALID_VISIBILITY.has(visibility)) throw new Error('updateSavedView: invalid visibility');
    patch.visibility = visibility;
  }
  if (viewState != null) patch.view_state = viewState;
  if (Object.keys(patch).length === 0) throw new Error('updateSavedView: nothing to update');
  // RLS + the freeze trigger guarantee only the owner can update and that
  // owner_profile_id / created_at are not mutated here.
  const {data, error} = await sb
    .from('app_saved_views')
    .update(patch)
    .eq('id', id)
    .select('id, surface_key, name, visibility, view_state, owner_profile_id, created_at, updated_at')
    .single();
  if (error) throw new Error(`updateSavedView: ${error.message || String(error)}`);
  return data;
}

export async function deleteSavedView(sb: SupabaseClient, id: string): Promise<boolean> {
  if (!sb) throw new Error('deleteSavedView: sb required');
  if (!id) throw new Error('deleteSavedView: id required');
  // RLS rejects deletes the caller does not own.
  const {error} = await sb.from('app_saved_views').delete().eq('id', id);
  if (error) throw new Error(`deleteSavedView: ${error.message || String(error)}`);
  return true;
}
